/**
 * ID generation utilities - Consolidated ID generation logic
 */

/**
 * The default id source: the original browser implementation, kept verbatim so
 * nothing about the running app changes.
 */
const defaultIdSource = () => {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

let idSource = defaultIdSource;

/**
 * Swap the id source.
 *
 * Builder ids are internal handles, they never reach the Flow JSON sent to
 * Meta. But they DO reach the JSON that hydrate() produces, so a differential
 * test comparing the browser output byte-for-byte against the JVM output needs
 * both hosts to mint the same ids. Injecting a seeded generator is what makes
 * those comparisons stable. Pass nothing to restore the default.
 *
 * @param {(() => string)|null|undefined} source
 */
export const setIdSource = (source) => {
  idSource = typeof source === 'function' ? source : defaultIdSource;
};

/**
 * Generate unique component ID
 */
export const generateId = () => {
  return idSource();
};

/**
 * Generate alphabetical suffix (a, b, c, ..., z, aa, ab, ...)
 */
export const generateAlphabeticalSuffix = (count) => {
  let result = '';
  let num = count;
  
  while (num >= 0) {
    result = String.fromCharCode(97 + (num % 26)) + result; // 97 is 'a'
    num = Math.floor(num / 26) - 1;
    if (num < 0) break;
  }
  
  return result;
};

/**
 * Generate screen ID
 */
export const generateScreenId = (index) => {
  return `screen_${generateAlphabeticalSuffix(index)}`;
};

/**
 * Generate component field name
 */
export const generateComponentFieldName = (componentType, index) => {
  const cleanType = componentType.replace(/[^a-zA-Z]/g, ''); // Remove non-letters
  const suffix = generateAlphabeticalSuffix(index);
  return `${cleanType}_${suffix}`;
};

/**
 * Generate unique screen ID that doesn't conflict with existing ones
 */
export const generateUniqueScreenId = (existingIds) => {
  const existingIdSet = new Set(existingIds);
  let counter = 0;
  let newId;
  
  do {
    newId = generateScreenId(counter);
    counter++;
  } while (existingIdSet.has(newId));
  
  return newId;
};

/**
 * Generate flow ID
 */
export const generateFlowId = () => {
  return `flow_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`;
};